import { useEffect, useId, useRef, useState } from 'react';
import type { PersonaAppearance } from '@contracts/persona';
import { cn } from '../../lib/utils';
import type { Emotion } from './emotion';
import { HanaSvg, LOOKS } from './HanaParts';
import './hana.css';

/** Four blink rhythms (4.5 s, 3.9 s, 5.2 s, 6 s): each Hana picks one, so two on a page never blink in step. */
export const BLINK_CLASSES: readonly string[] = ['hana-blink-a', 'hana-blink-b', 'hana-blink-c', 'hana-blink-d'];

export type HanaProps = {
  emotion: Emotion;
  /** 40 in the panel header, 28 on the header button (D119). */
  size: number;
  /** The person's "giảm chuyển động" pref: no body motion, no blink, no eyes following. The OS setting is CSS's. */
  still?: boolean;
  /** The pupils follow the pointer (the mockup's always-on behaviour; the big panel Hana only). */
  follow?: boolean;
  className?: string;
  /**
   * S8 §9, D93: the look, required with no default so the compiler names every site. The reader's own assistant
   * passes `useOwnAppearance()`; anyone else's assistant is never a figure (D92, `AssistantBadge`).
   */
  appearance: PersonaAppearance;
};

/**
 * Hana, the assistant's character (spec §7): the mockup's face, its body animation for the emotion, a blink, and the
 * pupils looking toward the pointer by at most 3.2 units. Decorative: the words say everything she shows.
 */
export function Hana({ emotion, size, still = false, follow = false, className, appearance }: HanaProps) {
  const root = useRef<HTMLSpanElement>(null);
  // Only ASCII letters and digits: React's id carries characters a `url(#…)` reference should not have to escape.
  const gradientId = `hana-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  // A per-instance blink rhythm, picked once: the blink looks random without an inline style (CSP).
  const [blink] = useState(() => BLINK_CLASSES[Math.floor(Math.random() * BLINK_CLASSES.length)]!);
  const look = LOOKS[emotion];
  useEffect(() => {
    if (!follow || still) return undefined;
    let frame = 0;
    const onMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = root.current;
        if (el === null) return;
        const r = el.getBoundingClientRect();
        const dx = event.clientX - (r.left + r.width / 2);
        const dy = event.clientY - (r.top + r.height / 2);
        const d = Math.hypot(dx, dy) || 1;
        const m = Math.min(3.2, d / 40);
        el.querySelectorAll<SVGCircleElement>('.pupil').forEach((p) => {
          p.setAttribute('cx', String(Number(p.dataset.cx) + (dx / d) * m));
          p.setAttribute('cy', String(Number(p.dataset.cy) + (dy / d) * m));
        });
      });
    };
    document.addEventListener('pointermove', onMove);
    return () => { document.removeEventListener('pointermove', onMove); cancelAnimationFrame(frame); };
  }, [follow, still, emotion]);
  return (
    <span ref={root} aria-hidden="true" data-emotion={emotion} data-still={still ? '' : undefined}
      className={cn('hana', size <= 28 && 'hana-sm', !still && `a-${look.a}`, blink, className)}>
      <HanaSvg emotion={emotion} size={size} gradientId={gradientId} appearance={appearance} />
    </span>
  );
}
